import { useState, useEffect } from 'react';
import {
  Plus,
  Pencil,
  Trash2,
  X,
  ShieldCheck,
  Search,
  Check,
  AlertCircle,
  Loader2
} from 'lucide-react';
import axios from 'axios';
import { detectPerms } from '../../permissions';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const initialFormData = {
  name: '',
  description: '',
  active: true
};

export const Warranty = () => {
  const perms = detectPerms();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [searchQuery, setSearchQuery] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  });

  useEffect(() => { fetchItems(); }, []);

  const fetchItems = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${BACKEND_URL}/api/warranties`, getAuthHeaders());
      setItems(res.data);
    } catch (err) {
      setError('Failed to fetch warranties');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const openAddModal = () => {
    setFormData(initialFormData);
    setEditingItem(null);
    setError('');
    setShowModal(true);
  };

  const openEditModal = (item) => {
    setFormData({
      name: item.name || '',
      description: item.description || '',
      active: item.active ?? true
    });
    setEditingItem(item);
    setError('');
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!formData.name.trim()) {
      setError('Warranty name is required');
      return;
    }

    // Client-side duplicate-name guard (case-insensitive, trimmed)
    const trimmed = formData.name.trim().toLowerCase();
    const duplicate = items.find(i =>
      i.name?.trim().toLowerCase() === trimmed &&
      (!editingItem || i.id !== editingItem.id)
    );
    if (duplicate) {
      setError('A warranty with this name already exists');
      return;
    }

    setSubmitting(true);
    try {
      if (editingItem) {
        await axios.put(`${BACKEND_URL}/api/warranties/${editingItem.id}`, formData, getAuthHeaders());
      } else {
        await axios.post(`${BACKEND_URL}/api/warranties`, formData, getAuthHeaders());
      }
      setShowModal(false);
      fetchItems();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save warranty');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/warranties/${id}`, getAuthHeaders());
      setDeleteConfirm(null);
      fetchItems();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete warranty');
    }
  };

  const filtered = items.filter(i =>
    i.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    i.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div data-testid="warranty-page">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 uppercase tracking-tight">Warranty</h1>
          <p className="text-slate-600 mt-1">Manage warranty types &amp; terms</p>
        </div>
        {perms.canAdd && (
        <button
          onClick={openAddModal}
          data-testid="add-warranty-btn"
          className="inline-flex items-center gap-2 bg-orange-500 text-white font-bold tracking-wider uppercase px-6 py-3 rounded-none hover:bg-orange-600 transition-colors"
        >
          <Plus className="h-5 w-5" />
          Add Warranty
        </button>
        )}
      </div>

      {/* Search Bar */}
      <div className="bg-white rounded-sm border border-slate-200 p-4 mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
          <input
            type="text"
            placeholder="Search warranties..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            data-testid="search-input"
            className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none transition-colors"
          />
        </div>
      </div>

      {/* Error Banner */}
      {error && !showModal && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm mb-6">
          <AlertCircle className="h-5 w-5" />
          <span>{error}</span>
          <button onClick={() => setError('')} className="ml-auto"><X className="h-4 w-4" /></button>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-sm border border-slate-200">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16" data-testid="empty-state">
            <ShieldCheck className="h-16 w-16 mx-auto mb-4 text-slate-300" />
            <h3 className="text-xl font-semibold text-slate-700 mb-2">No warranties yet</h3>
            <p className="text-slate-500 mb-6">Start by adding your first warranty type</p>
            {perms.canAdd && (
            <button
              onClick={openAddModal}
              data-testid="empty-add-btn"
              className="inline-flex items-center gap-2 bg-orange-500 text-white font-bold tracking-wider uppercase px-6 py-3 rounded-none hover:bg-orange-600 transition-colors"
            >
              <Plus className="h-5 w-5" />
              Add First Warranty
            </button>
            )}
          </div>
        ) : (
          <>
            <div className="hidden md:grid grid-cols-4 gap-4 px-6 py-4 border-b border-slate-200 bg-slate-50">
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Name</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Description</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Status</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Actions</div>
            </div>
            {filtered.map((item) => (
              <div
                key={item.id}
                data-testid={`warranty-row-${item.id}`}
                className="grid grid-cols-1 md:grid-cols-4 gap-4 px-6 py-4 border-b border-slate-100 hover:bg-slate-50 items-center"
              >
                <div className="font-medium text-slate-900">{item.name}</div>
                <div className="text-slate-600 text-sm truncate">{item.description || '-'}</div>
                <div>
                  <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${
                    item.active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                  }`}>
                    {item.active ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
                    {item.active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {perms.canEdit && (
                  <button
                    onClick={() => openEditModal(item)}
                    data-testid={`edit-btn-${item.id}`}
                    className="p-2 text-slate-600 hover:text-orange-500 hover:bg-orange-50 rounded transition-colors"
                    title="Edit"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  )}
                  {perms.canDelete && (
                  <button
                    onClick={() => setDeleteConfirm(item.id)}
                    data-testid={`delete-btn-${item.id}`}
                    className="p-2 text-slate-600 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                    title="Delete"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                  )}
                </div>
              </div>
            ))}
          </>
        )}
      </div>

      {/* Delete Confirmation */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-sm p-6 max-w-md w-full">
            <h3 className="text-xl font-bold text-slate-900 mb-4">Confirm Delete</h3>
            <p className="text-slate-600 mb-6">Are you sure you want to delete this warranty? This action cannot be undone.</p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 border border-slate-200 text-slate-700 rounded-sm hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                data-testid="confirm-delete-btn"
                className="px-4 py-2 bg-red-500 text-white rounded-sm hover:bg-red-600 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-sm w-full max-w-lg my-8">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h2 className="text-xl font-bold text-slate-900 uppercase tracking-tight">
                {editingItem ? 'Edit Warranty' : 'Add New Warranty'}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="p-2 text-slate-400 hover:text-slate-600 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6">
              {error && (
                <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm mb-6">
                  <AlertCircle className="h-5 w-5" />
                  <span>{error}</span>
                </div>
              )}

              <div className="space-y-4">
                {/* Name with typeahead suggestions */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      onFocus={() => setShowSuggestions(true)}
                      onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                      autoComplete="off"
                      data-testid="input-name"
                      className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none"
                      required
                    />
                    {showSuggestions && formData.name.trim() && (() => {
                      const q = formData.name.trim().toLowerCase();
                      const matches = items
                        .filter(i => i.name?.toLowerCase().includes(q))
                        .filter(i => !editingItem || i.id !== editingItem.id)
                        .slice(0, 8);
                      const exactMatch = matches.find(i => i.name?.toLowerCase() === q);
                      if (matches.length === 0) return null;
                      return (
                        <div
                          data-testid="name-suggestions"
                          className="absolute left-0 right-0 top-full mt-1 bg-white border border-slate-200 rounded-sm shadow-lg z-10 max-h-56 overflow-y-auto"
                        >
                          {exactMatch && (
                            <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border-b border-amber-200 text-amber-800 text-xs font-medium">
                              <AlertCircle className="h-3.5 w-3.5" />
                              A warranty with this exact name already exists.
                            </div>
                          )}
                          {matches.map(i => {
                            const isExact = i.name?.toLowerCase() === q;
                            return (
                              <button
                                key={i.id}
                                type="button"
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => {
                                  setFormData(prev => ({ ...prev, name: i.name }));
                                  setShowSuggestions(false);
                                }}
                                data-testid={`suggestion-${i.id}`}
                                className={`w-full text-left px-3 py-2 text-sm hover:bg-orange-50 flex items-center justify-between border-b border-slate-100 last:border-b-0 ${
                                  isExact ? 'bg-amber-50/40' : ''
                                }`}
                              >
                                <span className="text-slate-800">{i.name}</span>
                                {!i.active && (
                                  <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 bg-red-100 text-red-700 rounded">Inactive</span>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      );
                    })()}
                  </div>
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows={3}
                    data-testid="input-description"
                    className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none"
                  />
                </div>

                {/* Active */}
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="active"
                    name="active"
                    checked={formData.active}
                    onChange={handleInputChange}
                    data-testid="input-active"
                    className="w-5 h-5 text-orange-500 border-slate-300 rounded focus:ring-orange-500"
                  />
                  <label htmlFor="active" className="text-sm font-medium text-slate-700">Active</label>
                </div>
              </div>

              <div className="flex gap-3 justify-end pt-6 mt-6 border-t border-slate-200">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-6 py-2 border border-slate-200 text-slate-700 rounded-sm hover:bg-slate-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="submit-btn"
                  className="px-6 py-2 bg-orange-500 text-white font-bold uppercase tracking-wider rounded-sm hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
                >
                  {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                  {editingItem ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
